/**
 * Shoebox3DViewer — V14.63 Sprint 26F (vanilla three.js rewrite).
 *
 * Renders a single floor's micro-IFC payload using vanilla three.js
 * (no R3F / drei) to bypass the Emergent dev-preview source-tracking
 * attribute injection that crashed R3F's applyProps. See
 * `useThreeViewer.js` for the rationale.
 */
import { useMemo } from 'react';
import * as THREE from 'three';
import {
  WALL_COLORS, STAIRS_COLOR, WINDOW_COLOR, DOOR_COLOR,
  FLOOR_SLAB_COLOR, TERRAIN_COLOR,
  computeCentroidPx, polygonToShape, boundsPx, resolveFloorHeight,
  effectiveWallHeight, buildGuardrail,
  buildVirtualWallLine,
} from './geomUtils';
import { useThreeViewer, disposeGroup } from './useThreeViewer';
import { addSatelliteGroundPlane } from './SatelliteGroundPlane';

// ─────── Imperative builders ───────

const buildSlab = ({ walls, terrain, scaleMPerPx, cx, cy, showTerrain, palette }) => {
  const group = new THREE.Group();
  if (showTerrain) {
    const tShape = polygonToShape({ polygon: terrain, scaleMPerPx, cx, cy });
    if (tShape) {
      const mesh = new THREE.Mesh(
        new THREE.ShapeGeometry(tShape),
        new THREE.MeshStandardMaterial({ color: TERRAIN_COLOR, roughness: 0.95 }),
      );
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.y = -0.04;
      mesh.receiveShadow = true;
      group.add(mesh);
    }
  }
  // Convex-hull slab.
  const pts = [];
  for (const w of walls) {
    pts.push(new THREE.Vector2((w.x1 - cx) * scaleMPerPx, (w.y1 - cy) * scaleMPerPx));
    pts.push(new THREE.Vector2((w.x2 - cx) * scaleMPerPx, (w.y2 - cy) * scaleMPerPx));
  }
  if (pts.length >= 3) {
    const sorted = [...pts].sort((a, b) => a.x - b.x || a.y - b.y);
    const cross = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
    const lower = [];
    for (const p of sorted) {
      while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
      lower.push(p);
    }
    const upper = [];
    for (let i = sorted.length - 1; i >= 0; i -= 1) {
      const p = sorted[i];
      while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
      upper.push(p);
    }
    const hull = [...lower.slice(0, -1), ...upper.slice(0, -1)];
    if (hull.length >= 3) {
      const slab = new THREE.Mesh(
        new THREE.ShapeGeometry(new THREE.Shape(hull)),
        new THREE.MeshStandardMaterial({ color: palette?.slab || FLOOR_SLAB_COLOR, roughness: 0.85 }),
      );
      slab.rotation.x = -Math.PI / 2;
      slab.position.y = -0.005;
      slab.receiveShadow = true;
      group.add(slab);
    }
  }
  return group;
};

const buildWalls = ({ walls, scaleMPerPx, height, cx, cy, palette }) => {
  const group = new THREE.Group();
  for (const w of walls) {
    const mx1 = (w.x1 - cx) * scaleMPerPx;
    const mz1 = (w.y1 - cy) * scaleMPerPx;
    const mx2 = (w.x2 - cx) * scaleMPerPx;
    const mz2 = (w.y2 - cy) * scaleMPerPx;
    const dx = mx2 - mx1, dz = mz2 - mz1;
    const length = Math.hypot(dx, dz);
    if (length < 0.01) continue;
    // V14.96 — Mur invisible : simple pointillé au sol, aucun volume.
    if (w.kind === 'mur_invisible') {
      const line = buildVirtualWallLine(THREE, { x1: mx1, z1: mz1, x2: mx2, z2: mz2, y: 0.01 });
      group.add(line);
      continue;
    }
    const isBar = w.kind === 'cloison_basse' && w.railing_style === 'comptoir';
    const thicknessM = isBar ? Math.max(0.3, +w.bar_depth_m || 0.6) : Math.max(0.05, (w.thickness || 8) * scaleMPerPx);
    // Sprint 30 — Mansarde : mur bas d'un rampant. V14.91 — cloison basse 1,10 m.
    const wallH = effectiveWallHeight(w, height);
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(length, wallH, thicknessM),
      new THREE.MeshStandardMaterial({
        color: palette?.wall || WALL_COLORS[w.kind] || WALL_COLORS.default,
        roughness: 0.7, metalness: 0.05,
      }),
    );
    mesh.position.set((mx1 + mx2) / 2, wallH / 2, (mz1 + mz2) / 2);
    mesh.rotation.y = -Math.atan2(dz, dx);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
    if (isBar) {
      const top = new THREE.Mesh(
        new THREE.BoxGeometry(length + 0.04, 0.04, thicknessM + 0.06),
        new THREE.MeshStandardMaterial({ color: palette ? '#fbfbfc' : '#e2e8f0', roughness: 0.4, metalness: 0.05 }),
      );
      top.position.set((mx1 + mx2) / 2, wallH + 0.02, (mz1 + mz2) / 2);
      top.rotation.y = -Math.atan2(dz, dx);
      group.add(top);
    } else if (w.kind === 'cloison_basse' && wallH < height - 0.3) {
      const rail = buildGuardrail(THREE, { length, baseY: wallH, style: w.railing_style, thickness: thicknessM });
      rail.position.set((mx1 + mx2) / 2, 0, (mz1 + mz2) / 2);
      rail.rotation.y = -Math.atan2(dz, dx);
      group.add(rail);
    }
  }
  return group;
};

const FIXTURE_COLORS = { ilot: '#f59e0b', plan_travail: '#fbbf24', cheminee: '#ef4444', colonne: '#94a3b8', placard: '#a78bfa', douche: '#38bdf8', baignoire: '#22d3ee' };

const buildOpenings = ({ walls, openings, scaleMPerPx, color, height, cx, cy, yFraction, sizeY }) => {
  const group = new THREE.Group();
  const wallById = Object.fromEntries(walls.map((w) => [w.id, w]));
  for (const o of openings || []) {
    const w = wallById[o.wallId];
    if (!w) continue;
    const mx1 = (w.x1 - cx) * scaleMPerPx;
    const mz1 = (w.y1 - cy) * scaleMPerPx;
    const mx2 = (w.x2 - cx) * scaleMPerPx;
    const mz2 = (w.y2 - cy) * scaleMPerPx;
    const dx = mx2 - mx1, dz = mz2 - mz1;
    const length = Math.hypot(dx, dz);
    if (length < 0.01) continue;
    const widthM = Math.max(0.4, (o.width || 0.2) * length);
    const tCenter = Math.max(0, Math.min(1, o.t || 0.5));
    const cxw = mx1 + dx * tCenter;
    const czw = mz1 + dz * tCenter;
    const isBar = w.kind === 'cloison_basse' && w.railing_style === 'comptoir';
    const thicknessM = isBar ? Math.max(0.3, +w.bar_depth_m || 0.6) : Math.max(0.05, (w.thickness || 8) * scaleMPerPx);
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(widthM, sizeY, thicknessM + 0.02),
      new THREE.MeshStandardMaterial({
        color, emissive: color, emissiveIntensity: 0.18, transparent: true, opacity: 0.85,
      }),
    );
    mesh.position.set(cxw, height * yFraction, czw);
    mesh.rotation.y = -Math.atan2(dz, dx);
    group.add(mesh);
  }
  return group;
};

const buildStairs = ({ stairs, scaleMPerPx, height, cx, cy, palette }) => {
  const group = new THREE.Group();
  for (const s of stairs || []) {
    const x = (s.x + (s.w || 0) / 2 - cx) * scaleMPerPx;
    const z = (s.y + (s.h || 0) / 2 - cy) * scaleMPerPx;
    const w = Math.max(0.6, (s.w || 80) * scaleMPerPx);
    const d = Math.max(0.6, (s.h || 80) * scaleMPerPx);
    const ramp = new THREE.Mesh(
      new THREE.BoxGeometry(w, height * 0.95, d),
      new THREE.MeshStandardMaterial({
        color: palette?.stairs || STAIRS_COLOR, transparent: !palette, opacity: palette ? 1 : 0.42, roughness: 0.5,
      }),
    );
    ramp.position.set(x, height / 2, z);
    group.add(ramp);
    for (const t of [0.25, 0.5, 0.75]) {
      const step = new THREE.Mesh(
        new THREE.BoxGeometry(w * 0.96, 0.04, d * 0.96),
        new THREE.MeshStandardMaterial({ color: palette?.stairs || STAIRS_COLOR, roughness: 0.4 }),
      );
      step.position.set(x, height * t, z);
      group.add(step);
    }
  }
  return group;
};

const buildGrid = ({ size = 40 } = {}) => {
  const helper = new THREE.GridHelper(size, size, 0x374151, 0x1f2937);
  helper.position.y = -0.005;
  return helper;
};

// ─────── Component ───────

const Shoebox3DViewer = ({
  floor,
  height: heightOverride,
  showGrid = true,
  showTerrain = true,
  background = '#0b1220',
  className = '',
  testId = 'shoebox-3d-viewer',
  projectId = null,        // Sprint 32
  showSatellite = false,   // Sprint 32
  satelliteAlignment = null, // Sprint 32.5
  whiteboxMode = false,      // V15.01 — maquette blanche : tout blanc, fond gris clair, sans grille/ouvertures colorées
  onReady = null,            // V15.01 — ({ capture(), setIsoCamera() }) pour la capture haute qualité
}) => {
  const geometry = floor?.geometry || {};
  const walls = geometry.walls || [];
  const terrain = geometry.terrain || [];
  const scaleMPerPx = Number.isFinite(geometry.scale_m_per_px) && geometry.scale_m_per_px > 0
    ? geometry.scale_m_per_px : 0.035;
  const height = heightOverride || resolveFloorHeight(floor);

  const { cx, cy, camPos, target, radius } = useMemo(() => {
    const c = computeCentroidPx(walls, terrain);
    const bb = boundsPx(walls, terrain);
    const wM = Math.max(2, bb.w * scaleMPerPx);
    const dM = Math.max(2, bb.h * scaleMPerPx);
    const r = Math.max(wM, dM) * 1.1;
    return {
      cx: c.cx, cy: c.cy,
      camPos: [r, r * 0.8, r],
      target: [0, height / 2, 0],
      radius: r,
    };
  }, [walls, terrain, scaleMPerPx, height]);

  const empty = walls.length === 0;

  const palette = whiteboxMode ? { wall: '#f4f4f5', slab: '#e4e4e7', stairs: '#ededf0' } : null;
  const { canvasRef } = useThreeViewer({
    background: whiteboxMode ? '#e2e3e5' : background,
    cameraInit: { pos: camPos, target },
    enableShadows: true,
    deps: [floor, height, showGrid, showTerrain, scaleMPerPx, showSatellite, projectId, satelliteAlignment?.rotation_deg, satelliteAlignment?.offset_x_m, satelliteAlignment?.offset_z_m, whiteboxMode],
    onCreate: ({ scene, camera, controls, renderer }) => {
      if (empty) return undefined;
      scene.background = new THREE.Color(whiteboxMode ? '#e2e3e5' : background);
      const root = new THREE.Group();
      root.add(buildSlab({ walls, terrain, scaleMPerPx, cx, cy, showTerrain: showTerrain && !whiteboxMode, palette }));
      root.add(buildWalls({ walls, scaleMPerPx, height, cx, cy, palette }));
      if (!whiteboxMode) root.add(buildOpenings({
        walls, openings: geometry.windows || [], scaleMPerPx,
        color: WINDOW_COLOR, height, cx, cy, yFraction: 0.5, sizeY: 1.1,
      }));
      if (!whiteboxMode) root.add(buildOpenings({
        walls, openings: geometry.doors || [], scaleMPerPx,
        color: DOOR_COLOR, height, cx, cy, yFraction: 0.4, sizeY: 2.0,
      }));
      root.add(buildStairs({ stairs: geometry.stairs || [], scaleMPerPx, height, cx, cy, palette }));
      // V15.08 — éléments fixes : blocs pleins (hauteur propre ou HSP).
      const fxGroup = new THREE.Group();
      (geometry.fixtures || []).forEach((f) => {
        const fw = Math.max(0.1, f.w * scaleMPerPx), fd = Math.max(0.1, f.h * scaleMPerPx);
        const fh = f.height_m > 0 ? Math.min(f.height_m, height) : height;
        const m = new THREE.Mesh(
          new THREE.BoxGeometry(fw, fh, fd),
          new THREE.MeshStandardMaterial({ color: palette ? palette.wall : (FIXTURE_COLORS[f.kind] || '#d4a373'), roughness: 0.7 }),
        );
        m.position.set((f.x + f.w / 2) * scaleMPerPx - cx, fh / 2, (f.y + f.h / 2) * scaleMPerPx - cy);
        m.rotation.y = -((+f.rotation_deg || 0) * Math.PI) / 180;
        fxGroup.add(m);
      });
      root.add(fxGroup);
      if (showGrid && !whiteboxMode) root.add(buildGrid({ size: 40 }));
      // V15.01 — API de capture : caméra iso 45°/35° (même azimut/élévation que la maquette Python) + PNG.
      const setIsoCamera = () => {
        const az = Math.PI / 4, el = (35 * Math.PI) / 180;
        const r = radius * 2.2;
        camera.position.set(Math.sin(az) * Math.cos(el) * r, Math.sin(el) * r + height / 2, Math.cos(az) * Math.cos(el) * r);
        controls.target.set(0, height / 2, 0);
        controls.update();
        renderer.render(scene, camera);
      };
      const capture = () => { renderer.render(scene, camera); return renderer.domElement.toDataURL('image/png'); };
      onReady?.({ capture, setIsoCamera });

      // Lights
      const ambient = new THREE.AmbientLight(0xffffff, 0.55);
      const dir = new THREE.DirectionalLight(0xffffff, 1.0);
      dir.position.set(10, 18, 8);
      dir.castShadow = true;
      if (dir.shadow) {
        dir.shadow.mapSize.width = 1024;
        dir.shadow.mapSize.height = 1024;
      }
      root.add(ambient);
      root.add(dir);

      scene.add(root);
      // Sprint 32 — satellite ground plane
      const cleanupSat = (showSatellite && projectId)
        ? addSatelliteGroundPlane({
            scene, projectId,
            radiusM: radius * 1.15,
            groundY: -0.015,
            opacity: 0.9,
            rotationDeg: satelliteAlignment?.rotation_deg || 0,
            offsetXM: satelliteAlignment?.offset_x_m || 0,
            offsetZM: satelliteAlignment?.offset_z_m || 0,
          })
        : null;
      return () => {
        if (cleanupSat) cleanupSat();
        disposeGroup(root);
      };
    },
  });

  if (empty) {
    return (
      <div className={`flex items-center justify-center text-gray-400 text-[12px] ${className}`}
           style={{ background, minHeight: 320 }}
           data-testid={testId}>
        Aucun mur tracé sur cet étage — Shoebox 3D indisponible.
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} style={{ background }} data-testid={testId}>
      <canvas ref={canvasRef}
              className="block w-full h-full"
              data-testid={`${testId}-canvas`} />
    </div>
  );
};

export default Shoebox3DViewer;
